/* =============================================
   LUNOVA LIVING — Main JavaScript
   ============================================= */

(function () {
  'use strict';

  /* ---------- CART ---------- */
  const Cart = {
    async get() {
      const res = await fetch('/cart.js');
      return res.json();
    },

    async add(variantId, quantity = 1) {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity }),
      });
      return res.json();
    },

    async change(key, quantity) {
      const res = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity }),
      });
      return res.json();
    },

    formatMoney(cents) {
      return '$' + (cents / 100).toLocaleString('es-MX', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
      }) + ' MXN';
    },
  };

  /* ---------- TOAST ---------- */
  const Toast = {
    el: null,
    timer: null,
    init() {
      this.el = document.getElementById('toast');
    },
    show(msg, duration = 2500) {
      if (!this.el) return;
      this.el.textContent = msg;
      this.el.classList.add('toast--show');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.el.classList.remove('toast--show'), duration);
    },
  };

  /* ---------- CART DRAWER ---------- */
  const CartDrawer = {
    drawer: null,
    overlay: null,

    init() {
      this.drawer = document.getElementById('cart-drawer');
      if (!this.drawer) return;

      // overlay
      this.overlay = document.createElement('div');
      this.overlay.className = 'cart-overlay';
      this.overlay.style.cssText =
        'position:fixed;inset:0;background:rgba(44,43,41,0.4);z-index:199;opacity:0;transition:opacity 0.3s;pointer-events:none;';
      document.body.appendChild(this.overlay);
      this.overlay.addEventListener('click', () => this.close());

      // close button
      this.drawer.querySelector('.cart-drawer__close')?.addEventListener('click', () => this.close());

      // open triggers
      document.querySelectorAll('[data-open-cart]').forEach((btn) =>
        btn.addEventListener('click', () => this.open())
      );
    },

    async open() {
      const cart = await Cart.get();
      this.render(cart);
      this.drawer.classList.add('cart-drawer--open');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.overlay.style.opacity = '1';
      this.overlay.style.pointerEvents = 'auto';
      document.body.style.overflow = 'hidden';
    },

    close() {
      this.drawer.classList.remove('cart-drawer--open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.overlay.style.opacity = '0';
      this.overlay.style.pointerEvents = 'none';
      document.body.style.overflow = '';
    },

    render(cart) {
      const itemsEl = this.drawer.querySelector('.cart-drawer__items');
      const footerEl = this.drawer.querySelector('.cart-drawer__footer');
      if (!itemsEl) return;

      if (cart.item_count === 0) {
        itemsEl.innerHTML =
          '<div class="cart-drawer__empty"><p style="margin-bottom:1rem">Tu carrito está vacío</p><a href="/collections/all" class="btn btn--dark btn--sm">Explorar productos</a></div>';
        if (footerEl) footerEl.style.display = 'none';
        return;
      }

      if (footerEl) footerEl.style.display = 'block';

      itemsEl.innerHTML = cart.items
        .map(
          (item) => `
        <div class="cart-item" data-key="${item.key}">
          <div class="cart-item__img">
            <img src="${item.image}" alt="${item.title}" loading="lazy">
          </div>
          <div>
            <div class="cart-item__title">${item.product_title}</div>
            <div class="cart-item__variant">${item.variant_title !== 'Default Title' ? item.variant_title : ''}</div>
            <div class="cart-item__bottom">
              <div class="qty-selector" style="transform:scale(0.85);transform-origin:left">
                <button class="qty-selector__btn" onclick="CartDrawer.updateQty('${item.key}', ${item.quantity - 1})">−</button>
                <input class="qty-selector__input" type="number" value="${item.quantity}" min="0"
                  onchange="CartDrawer.updateQty('${item.key}', this.value)" style="height:36px">
                <button class="qty-selector__btn" onclick="CartDrawer.updateQty('${item.key}', ${item.quantity + 1})">+</button>
              </div>
              <div class="cart-item__price">${Cart.formatMoney(item.final_line_price)}</div>
            </div>
            <button class="cart-item__remove" onclick="CartDrawer.updateQty('${item.key}', 0)">Eliminar</button>
          </div>
        </div>`
        )
        .join('');

      // subtotal & progress
      const FREE_SHIPPING = 99900; // $999 MXN in cents
      const remaining = FREE_SHIPPING - cart.total_price;
      const progress = Math.min((cart.total_price / FREE_SHIPPING) * 100, 100);

      if (footerEl) {
        footerEl.innerHTML = `
          <div class="cart-progress-bar"><div class="cart-progress-bar__fill" style="width:${progress}%"></div></div>
          <p class="cart-shipping-msg">
            ${remaining > 0
              ? `Agrega <strong>${Cart.formatMoney(remaining)}</strong> más para envío gratis`
              : '<span style="color:var(--success)">✓ ¡Tienes envío gratis!</span>'}
          </p>
          <div class="cart-subtotal">
            <span>Subtotal</span>
            <span class="cart-subtotal__price">${Cart.formatMoney(cart.total_price)}</span>
          </div>
          <a href="/checkout" class="btn btn--accent btn--full" style="margin-bottom:8px">Proceder al pago →</a>
          <a href="/cart" class="btn btn--outline btn--full btn--sm">Ver carrito completo</a>
          <div style="display:flex;justify-content:center;gap:12px;margin-top:12px">
            <span style="font-size:10px;color:var(--stone)">🔒 Pago seguro</span>
            <span style="font-size:10px;color:var(--stone)">↩️ Devolución 30 días</span>
          </div>`;
      }

      this.updateCount(cart.item_count);
    },

    async updateQty(key, qty) {
      const cart = await Cart.change(key, parseInt(qty));
      this.render(cart);
    },

    updateCount(count) {
      document.querySelectorAll('.cart-count').forEach((el) => {
        el.textContent = count;
        el.style.display = count > 0 ? 'flex' : 'none';
      });
    },
  };

  /* ---------- QUICK ADD ---------- */
  async function quickAdd(btn) {
    const variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.textContent = '✓';
    try {
      await Cart.add(variantId);
      const cart = await Cart.get();
      CartDrawer.updateCount(cart.item_count);
      Toast.show('✓ Producto agregado al carrito');
      setTimeout(() => CartDrawer.open(), 400);
    } catch (e) {
      Toast.show('Error al agregar producto');
    }
    setTimeout(() => {
      btn.disabled = false;
      btn.textContent = '+';
    }, 1500);
  }

  /* ---------- PRODUCT PAGE ---------- */
  function initProductPage() {
    // Thumbnail gallery
    const mainImg = document.querySelector('.product-gallery__main img');
    document.querySelectorAll('.product-gallery__thumb').forEach((thumb) => {
      thumb.addEventListener('click', () => {
        document.querySelectorAll('.product-gallery__thumb').forEach((t) =>
          t.classList.remove('product-gallery__thumb--active')
        );
        thumb.classList.add('product-gallery__thumb--active');
        if (mainImg) {
          mainImg.src = thumb.querySelector('img')?.src || '';
          mainImg.srcset = '';
        }
      });
    });

    // Color swatches
    document.querySelectorAll('.color-swatch').forEach((swatch) => {
      swatch.addEventListener('click', () => {
        document.querySelectorAll('.color-swatch').forEach((s) =>
          s.classList.remove('color-swatch--active')
        );
        swatch.classList.add('color-swatch--active');
      });
    });

    // Variant buttons
    document.querySelectorAll('.variant-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const group = btn.closest('.variant-buttons');
        group?.querySelectorAll('.variant-btn').forEach((b) =>
          b.classList.remove('variant-btn--active')
        );
        btn.classList.add('variant-btn--active');
      });
    });

    // Qty selector
    document.querySelectorAll('.qty-selector__btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.closest('.qty-selector')?.querySelector('.qty-selector__input');
        if (!input) return;
        const current = parseInt(input.value) || 1;
        const delta = btn.textContent.trim() === '+' ? 1 : -1;
        input.value = Math.max(1, current + delta);
      });
    });

    // Add to cart (product page)
    document.querySelector('[data-add-to-cart]')?.addEventListener('click', async (e) => {
      const btn = e.currentTarget;
      const variantId = btn.dataset.variantId;
      const qty = parseInt(document.querySelector('.qty-selector__input')?.value || 1);
      if (!variantId) return;
      btn.disabled = true;
      const originalText = btn.textContent;
      btn.textContent = 'Agregando...';
      try {
        await Cart.add(variantId, qty);
        const cart = await Cart.get();
        CartDrawer.updateCount(cart.item_count);
        btn.textContent = '✓ Agregado';
        Toast.show('✓ Producto agregado al carrito');
        setTimeout(() => CartDrawer.open(), 400);
      } catch {
        Toast.show('Error al agregar');
        btn.textContent = originalText;
      }
      setTimeout(() => {
        btn.disabled = false;
        btn.textContent = originalText;
      }, 2000);
    });
  }

  /* ---------- MOBILE NAV ---------- */
  function initMobileNav() {
    const toggle = document.querySelector('[data-nav-toggle]');
    const nav = document.querySelector('.mobile-nav');
    if (!toggle || !nav) return;
    toggle.addEventListener('click', () => {
      const open = nav.classList.toggle('mobile-nav--open');
      toggle.setAttribute('aria-expanded', open);
    });
  }

  /* ---------- SEARCH ---------- */
  function initSearch() {
    const toggle = document.querySelector('[data-search-toggle]');
    const panel = document.querySelector('.search-panel');
    if (!toggle || !panel) return;
    toggle.addEventListener('click', () => {
      panel.classList.toggle('search-panel--open');
      panel.querySelector('input')?.focus();
    });
  }

  /* ---------- COLLECTION FILTERS ---------- */
  function initFilters() {
    const priceRange = document.getElementById('filter-price');
    const priceLabel = document.getElementById('filter-price-label');
    if (priceRange && priceLabel) {
      priceRange.addEventListener('input', () => {
        priceLabel.textContent = '$' + parseInt(priceRange.value).toLocaleString();
      });
    }
  }

  /* ---------- STICKY HEADER SHADOW ---------- */
  function initStickyHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;
    const observer = new IntersectionObserver(
      ([e]) => header.classList.toggle('site-header--scrolled', !e.isIntersecting),
      { threshold: 1, rootMargin: '-1px 0px 0px 0px' }
    );
    const sentinel = document.createElement('div');
    sentinel.style.height = '1px';
    document.body.prepend(sentinel);
    observer.observe(sentinel);
  }

  /* ---------- INIT ---------- */
  document.addEventListener('DOMContentLoaded', () => {
    Toast.init();
    CartDrawer.init();
    initProductPage();
    initMobileNav();
    initSearch();
    initFilters();
    initStickyHeader();

    // Quick add buttons
    document.querySelectorAll('[data-quick-add]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        quickAdd(btn);
      });
    });
  });

  // Expose for inline calls
  window.CartDrawer = CartDrawer;
})();
